<template>
  <div>
    <HrPageLayout
      title="Announcements & Updates"
      subtitle="Company communications and news"
      icon="📢"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'Announcements' }
      ]"
    >
      <div class="employee-announcements-content">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-bullhorn"></i></div>
            <p class="hr-metric-label">Total Announcements</p>
            <h2 class="hr-metric-value">{{ statistics.total }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-envelope"></i></div>
            <p class="hr-metric-label">Unread</p>
            <h2 class="hr-metric-value">{{ statistics.unread }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-calendar"></i></div>
            <p class="hr-metric-label">This Week</p>
            <h2 class="hr-metric-value">{{ statistics.thisWeek }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
            <p class="hr-metric-label">Important</p>
            <h2 class="hr-metric-value">{{ statistics.important }}</h2>
          </div>
        </div>

        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body hr-py-3">
            <div class="ann-filters">
              <div class="hr-filter-group">
                <label class="hr-form-label">Category</label>
                <select v-model="filters.category" @change="loadAnnouncements" class="hr-select">
                  <option value="">All Categories</option>
                  <option value="General">General</option>
                  <option value="HR">HR Updates</option>
                  <option value="IT">IT/Technical</option>
                  <option value="Events">Events</option>
                  <option value="Policy">Policy Changes</option>
                  <option value="Training">Training</option>
                  <option value="Safety">Safety & Security</option>
                </select>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Priority</label>
                <select v-model="filters.priority" @change="loadAnnouncements" class="hr-select">
                  <option value="">All Priorities</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Normal">Normal</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Status</label>
                <select v-model="filters.status" @change="loadAnnouncements" class="hr-select">
                  <option value="">All</option>
                  <option value="unread">Unread Only</option>
                  <option value="read">Read</option>
                </select>
              </div>
              <button class="hr-btn hr-btn-outline hr-btn-primary" @click="markAllAsRead" :disabled="statistics.unread === 0">
                <i class="bx bx-check-double"></i> Mark All as Read
              </button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading announcements...</p>
        </div>

        <div v-else>
          <div v-if="filteredAnnouncements.length === 0" class="hr-chart-card hr-text-center hr-py-5">
            <i class="bx bx-info-circle" style="font-size: 48px; color: #059669;"></i>
            <h5 class="hr-mt-4">No Announcements Found</h5>
            <p class="hr-text-muted">There are no announcements matching your filters.</p>
          </div>

          <div v-for="announcement in filteredAnnouncements" :key="announcement.id" :id="'announcement-' + announcement.id"
               class="ann-card" :class="{ 'unread': !announcement.isRead }">
            <div class="ann-card-body">
              <div class="ann-card-inner">
                <div class="ann-icon" :class="getCategoryBadgeClass(announcement.category)">
                  <i :class="getCategoryIcon(announcement.category)"></i>
                </div>
                <div class="ann-content">
                  <div class="ann-header">
                    <div>
                      <h5 class="ann-title">
                        {{ announcement.title }}
                        <span v-if="!announcement.isRead" class="hr-badge hr-badge-danger hr-ml-2">New</span>
                        <span v-if="announcement.priority === 'Critical'" class="hr-badge hr-badge-danger hr-ml-2">
                          <i class="bx bx-error-circle"></i> Critical
                        </span>
                        <span v-else-if="announcement.priority === 'High'" class="hr-badge hr-badge-warning hr-ml-2">
                          <i class="bx bx-error"></i> High Priority
                        </span>
                      </h5>
                      <div class="hr-text-muted hr-small">
                        <span class="hr-badge hr-badge-secondary hr-mr-2">{{ announcement.category }}</span>
                        <i class="bx bx-calendar"></i> {{ formatDate(announcement.publishDate) }}
                        <span class="hr-mx-2">•</span>
                        <i class="bx bx-user"></i> {{ announcement.postedBy }}
                      </div>
                    </div>
                    <div class="ann-actions">
                      <button @click="toggleRead(announcement)" class="hr-btn hr-btn-sm"
                              :class="announcement.isRead ? 'hr-btn-outline' : 'hr-btn-primary'"
                              :title="announcement.isRead ? 'Mark as Unread' : 'Mark as Read'">
                        <i class="bx" :class="announcement.isRead ? 'bx-envelope-open' : 'bx-envelope'"></i>
                      </button>
                      <button @click="viewAnnouncement(announcement)" class="hr-btn hr-btn-sm hr-btn-primary">
                        <i class="bx bx-show"></i> View
                      </button>
                    </div>
                  </div>
                  <p v-if="announcement.shortDescription" class="hr-text-muted hr-mb-2">{{ announcement.shortDescription }}</p>
                  <div v-if="expandedAnnouncements.includes(announcement.id)" class="ann-expanded">
                    <hr class="ann-hr">
                    <div class="ann-content-html" v-html="announcement.content"></div>

                    <div class="ann-comments-section hr-mt-4">
                      <div class="ann-detail-section-header">
                        <span><i class="bx bx-comment-dots"></i> Comments</span>
                        <span class="ann-count-badge">{{ announcement.comments?.length || 0 }}</span>
                      </div>
                      <div v-if="announcement.comments && announcement.comments.length > 0" class="ann-comments-list">
                        <div v-for="comment in announcement.comments" :key="comment.id" class="ann-comment">
                          <div class="ann-comment-avatar">
                            {{ (comment.employee_name || 'U').split(' ').map(name => name[0]).join('').toUpperCase() }}
                          </div>
                          <div class="ann-comment-body">
                            <div class="ann-comment-header">
                              <span class="ann-comment-author">{{ comment.employee_name }}</span>
                              <span class="ann-comment-time">{{ formatDate(comment.created_at) }}</span>
                            </div>
                            <p class="ann-comment-text">{{ comment.comment }}</p>
                          </div>
                        </div>
                      </div>
                      <div v-else class="ann-empty-small">
                        <i class="bx bx-comment"></i>
                        <span>No comments yet</span>
                      </div>
                      <div class="ann-comment-form hr-mt-3">
                        <textarea v-model="commentForms[announcement.id]" class="hr-textarea" rows="3" placeholder="Add your comment..."></textarea>
                        <button class="hr-btn hr-btn-primary hr-btn-sm hr-mt-2" :disabled="!commentForms[announcement.id]?.trim()" @click="addComment(announcement)">
                          <i class="bx bx-send"></i> Post comment
                        </button>
                      </div>
                    </div>

                    <div v-if="announcement.attachments?.length" class="ann-attachments hr-mt-3">
                      <h6 class="hr-mb-2"><i class="bx bx-paperclip"></i> Attachments</h6>
                      <div class="ann-attach-list">
                        <a v-for="(att, idx) in announcement.attachments" :key="idx" :href="getAttachmentUrl(att)"
                           target="_blank" class="hr-btn hr-btn-sm hr-btn-outline">
                          <i class="bx bx-file"></i> {{ att.name }}
                        </a>
                      </div>
                    </div>
                    <a v-if="announcement.actionLink" :href="announcement.actionLink" class="hr-btn hr-btn-success hr-mt-3">
                      <i class="bx bx-link-external"></i> {{ announcement.actionText || 'Take Action' }}
                    </a>
                  </div>
                  <button @click="toggleExpand(announcement.id)" class="hr-btn hr-btn-link hr-p-0 hr-mt-2">
                    <span v-if="!expandedAnnouncements.includes(announcement.id)">
                      <i class="bx bx-chevron-down"></i> Read More
                    </span>
                    <span v-else>
                      <i class="bx bx-chevron-up"></i> Show Less
                    </span>
                  </button>
                </div>
              </div>
            </div>
            <div v-if="announcement.requiresAcknowledgment || announcement.reactions" class="ann-footer">
              <div class="ann-footer-inner">
                <div v-if="announcement.requiresAcknowledgment">
                  <button v-if="!announcement.acknowledged" @click="acknowledgeAnnouncement(announcement.id)"
                          class="hr-btn hr-btn-sm hr-btn-primary">
                    <i class="bx bx-check"></i> Acknowledge Receipt
                  </button>
                  <span v-else class="hr-badge hr-badge-success">
                    <i class="bx bx-check-circle"></i> Acknowledged
                  </span>
                </div>
                <div class="hr-text-muted hr-small">
                  <i class="bx bx-show"></i> {{ announcement.views || 0 }} views
                </div>
              </div>
            </div>
          </div>

          <div v-if="totalPages > 1" class="hr-pagination-wrap hr-mt-4">
            <div class="hr-pagination-info">
              Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalAnnouncements) }}
              of {{ totalAnnouncements }} announcements
            </div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
                <i class="bx bx-chevron-left"></i>
              </button>
              <button v-for="page in visiblePages" :key="page" type="button" class="hr-page-btn"
                      :class="{ 'hr-page-active': currentPage === page }" @click="changePage(page)">{{ page }}</button>
              <button type="button" class="hr-page-btn" :disabled="currentPage === totalPages"
                      @click="changePage(currentPage + 1)">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, uploads } from "@/api";

export default {
  name: "EmployeeAnnouncements",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      filters: { category: '', priority: '', status: '' },
      statistics: { total: 0, unread: 0, thisWeek: 0, important: 0 },
      announcements: [],
      expandedAnnouncements: [],
      commentForms: {},
      currentPage: 1,
      itemsPerPage: 10,
      totalAnnouncements: 0
    };
  },
  computed: {
    filteredAnnouncements() {
      let filtered = [...this.announcements];
      if (this.filters.status === 'unread') filtered = filtered.filter(a => !a.isRead);
      else if (this.filters.status === 'read') filtered = filtered.filter(a => a.isRead);
      return filtered;
    },
    totalPages() {
      return Math.ceil(this.totalAnnouncements / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  mounted() {
    this.loadAnnouncements();
  },
  methods: {
    async loadAnnouncements() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeAnnouncements");
        data.append("category", this.filters.category);
        data.append("priority", this.filters.priority);
        data.append("page", this.currentPage);
        data.append("itemsPerPage", this.itemsPerPage);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.announcements = res.data.data.announcements;
          this.statistics = res.data.data.statistics;
          this.totalAnnouncements = res.data.data.total;
        }
      } catch (e) {
        this.$toast?.error("Error loading announcements");
      } finally {
        this.loading = false;
      }
    },
    toggleExpand(announcementId) {
      const idx = this.expandedAnnouncements.indexOf(announcementId);
      if (idx > -1) {
        this.expandedAnnouncements.splice(idx, 1);
      } else {
        this.expandedAnnouncements.push(announcementId);
        const announcement = this.announcements.find(x => x.id === announcementId);
        if (announcement && !announcement.comments) {
          this.loadAnnouncementDetails(announcement);
        }
        this.markAsRead(announcementId);
      }
    },
    async loadAnnouncementDetails(announcement) {
      if (!announcement || !announcement.id) return;
      try {
        const data = new FormData();
        data.append("function", "getAnnouncementDetails");
        data.append("announcementId", announcement.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.announcement) {
          const details = res.data.data.announcement;
          announcement.comments = details.comments || [];
          announcement.attachments = details.attachmentsList || announcement.attachments || [];
          announcement.requiresAcknowledgment = details.require_acknowledgment;
          announcement.targetAudience = details.target_audience;
        }
      } catch (e) {
        /* ignore */
      }
    },
    async addComment(announcement) {
      const text = (this.commentForms[announcement.id] || '').trim();
      if (!text) {
        this.$toast?.error("Comment cannot be empty");
        return;
      }
      try {
        const data = new FormData();
        data.append("function", "addComment");
        data.append("announcementId", announcement.id);
        data.append("comment", text);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          announcement.comments = announcement.comments || [];
          announcement.comments.push({
            id: res.data.data.commentId,
            employee_name: res.data.data.employeeName,
            comment: text,
            created_at: res.data.data.created_at
          });
          this.commentForms[announcement.id] = '';
          this.$toast?.success(res.data.message);
        } else {
          this.$toast?.error(res.data.message || "Failed to post comment");
        }
      } catch (e) {
        this.$toast?.error("Error posting comment");
      }
    },
    async toggleRead(announcement) {
      try {
        const data = new FormData();
        data.append("function", "toggleAnnouncementRead");
        data.append("announcementId", announcement.id);
        data.append("isRead", announcement.isRead ? 0 : 1);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          announcement.isRead = !announcement.isRead;
          if (announcement.isRead) this.statistics.unread--;
          else this.statistics.unread++;
        }
      } catch (e) { /* ignore */ }
    },
    async markAsRead(announcementId) {
      const a = this.announcements.find(x => x.id === announcementId);
      if (a && !a.isRead) {
        try {
          const data = new FormData();
          data.append("function", "markAnnouncementAsRead");
          data.append("announcementId", announcementId);
          const res = await execute(data, authUrl);
          if (res?.data?.success) {
            a.isRead = true;
            this.statistics.unread--;
          }
        } catch (e) { /* ignore */ }
      }
    },
    async markAllAsRead() {
      try {
        const data = new FormData();
        data.append("function", "markAllAnnouncementsAsRead");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.announcements.forEach(a => a.isRead = true);
          this.statistics.unread = 0;
          this.$toast?.success("All announcements marked as read");
        }
      } catch (e) {
        this.$toast?.error("Error marking all as read");
      }
    },
    async acknowledgeAnnouncement(announcementId) {
      try {
        const data = new FormData();
        data.append("function", "acknowledgeAnnouncement");
        data.append("announcementId", announcementId);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          const a = this.announcements.find(x => x.id === announcementId);
          if (a) a.acknowledged = true;
          this.$toast?.success("Announcement acknowledged");
        }
      } catch (e) {
        this.$toast?.error("Error acknowledging");
      }
    },
    viewAnnouncement(announcement) {
      if (!this.expandedAnnouncements.includes(announcement.id)) this.toggleExpand(announcement.id);
      this.$nextTick(() => {
        const el = document.getElementById('announcement-' + announcement.id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadAnnouncements();
        window.scrollTo(0, 0);
      }
    },
    getCategoryBadgeClass(category) {
      const map = { General: 'ann-cat-primary', HR: 'ann-cat-info', IT: 'ann-cat-secondary', Events: 'ann-cat-success',
        Policy: 'ann-cat-warning', Training: 'ann-cat-info', Safety: 'ann-cat-danger' };
      return map[category] || 'ann-cat-primary';
    },
    getCategoryIcon(category) {
      const map = { General: 'bx bx-info-circle', HR: 'bx bx-user', IT: 'bx bx-laptop', Events: 'bx bx-calendar-event',
        Policy: 'bx bx-file', Training: 'bx bx-book', Safety: 'bx bx-shield' };
      return map[category] || 'bx bx-bullhorn';
    },
    formatDate(date) {
      if (!date) return '';

      let d;
      if (typeof date === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          const [year, month, day] = date.split('-').map(Number);
          d = new Date(year, month - 1, day);
        } else {
          d = new Date(date.replace(' ', 'T'));
        }
      } else {
        d = new Date(date);
      }

      if (Number.isNaN(d.getTime())) return '';

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diffDays = Math.round((today - dateOnly) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays > 1 && diffDays < 7) return diffDays + ' days ago';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    getAttachmentUrl(attachment) {
      if (attachment.path) return uploads + attachment.path;
      if (attachment.url) return attachment.url;
      return '#';
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.ann-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.ann-card { border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 16px; overflow: hidden; background: #fff; }
.ann-card.unread { border-left: 4px solid #ef4444; background: #fff8f8; }
.ann-card-body { padding: 20px; }
.ann-card-inner { display: flex; gap: 16px; }
.ann-icon { width: 56px; height: 56px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #fff; }
.ann-cat-primary { background: #3b82f6; }
.ann-cat-info { background: #0ea5e9; }
.ann-cat-secondary { background: #64748b; }
.ann-cat-success { background: #10b981; }
.ann-cat-warning { background: #f59e0b; }
.ann-cat-danger { background: #ef4444; }
.ann-icon i { font-size: 24px; }
.ann-content { flex: 1; min-width: 0; }
.ann-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 8px; }
.ann-title { margin: 0 0 4px 0; font-size: 16px; }
.ann-actions { display: flex; gap: 8px; }
.ann-hr { border: 0; border-top: 1px solid #e2e8f0; margin: 16px 0; }
.ann-content-html { line-height: 1.8; }
.ann-content-html :deep(p) { margin-bottom: 1rem; }
.ann-content-html :deep(ul), .ann-content-html :deep(ol) { margin-bottom: 1rem; padding-left: 2rem; }
.ann-attachments { padding-top: 12px; }
.ann-attach-list { display: flex; flex-wrap: wrap; gap: 8px; }
.ann-comments-section { padding: 18px; border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; }
.ann-detail-section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-weight: 700; color: #0f172a; }
.ann-count-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 30px; height: 30px; border-radius: 999px; background: #e2e8f0; color: #102a43; font-size: 0.85rem; padding: 0 10px; }
.ann-comments-list { display: flex; flex-direction: column; gap: 12px; }
.ann-comment { display: flex; gap: 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
.ann-comment-avatar { width: 42px; height: 42px; min-width: 42px; border-radius: 50%; background: #2563eb; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.95rem; }
.ann-comment-body { flex: 1; }
.ann-comment-header { display: flex; justify-content: space-between; gap: 12px; align-items: center; margin-bottom: 6px; flex-wrap: wrap; }
.ann-comment-author { font-weight: 700; color: #0f172a; }
.ann-comment-time { font-size: 0.85rem; color: #64748b; }
.ann-comment-text { margin: 0; color: #334155; line-height: 1.7; }
.ann-comment-form { display: flex; flex-direction: column; gap: 10px; }
.hr-textarea { width: 100%; min-height: 92px; padding: 12px; border-radius: 10px; border: 1px solid #cbd5e1; background: #fff; resize: vertical; }
.hr-textarea:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.ann-footer { padding: 12px 20px; background: #f8fafc; border-top: 1px solid #e2e8f0; }
.ann-footer-inner { display: flex; justify-content: space-between; align-items: center; }
.hr-ml-2 { margin-left: 8px; }
.hr-mr-2 { margin-right: 8px; }
.hr-mx-2 { margin: 0 8px; }
.hr-btn-outline { background: transparent; border: 1px solid #e2e8f0; }
.hr-btn-outline:hover { background: #f8fafc; border-color: #059669; }
</style>
